/** @format */

import moment from "moment";
import { TModalitaPresenze } from "../../../redux-modules/declarations/maps/turniSettimana";
import {
  TAzioneTimebank,
  TGiornoPresenze,
  TGiustificativoGiorno,
  TPermessoInAttesa,
  TPersonaPresenze,
  TPresenzaVista,
  TStatoPersonaGiorno,
  TStatoTurnoPresenze,
  TTurnoPresenze,
} from "../../../redux-modules/declarations/maps/presenzeVista";
import { normalizza, ora } from "../ViewTurniSettimana/format";

export { iniziali, nomeCompleto, normalizza, ora, tinta } from "../ViewTurniSettimana/format";

/**
 * Formati e testi della vista Presenze, senza interfaccia.
 *
 * Nessuna regola delle presenze qui: stati, segnalazioni e azioni sulle timbrature arrivano dal
 * backend. Il web sceglie solo come mostrarli e quale chiamata esistente usare per ogni azione.
 */

export type TUnitaPresenze = "ore" | "centesimi";
export type TFascia = "lavoro" | "giorno";
export type TVista = "giornata" | "da-gestire" | "mese";
export type TFiltroGiornata =
  | "tutti"
  | "in_servizio"
  | "da_iniziare"
  | "usciti"
  | "non_timbrati"
  | "assenti"
  | "da_gestire";

export const MENO = "−";
export const TRATTINO = "–";

/** Fascia oraria della linea, in minuti dalla mezzanotte del giorno mostrato. */
export const FASCE: Record<TFascia, { da: number; a: number; passo: number }> = {
  lavoro: { da: 7 * 60, a: 21 * 60, passo: 1 },
  giorno: { da: 0, a: 24 * 60, passo: 2 },
};

// ------------------------------------------------------------------ durate e orari

/**
 * Durata nel formato scelto: ore e minuti ("8 h 30") o centesimi ("8,50").
 * Il segno meno è quello tipografico; con conSegno i positivi hanno il "+".
 */
export const formatDurata = (minuti: number, unita: TUnitaPresenze, conSegno = false): string => {
  const assoluto = Math.abs(Math.round(minuti));
  let testo: string;
  if (unita === "centesimi") {
    testo = (assoluto / 60).toFixed(2).replace(".", ",");
  } else {
    const ore = Math.floor(assoluto / 60);
    const resto = assoluto % 60;
    testo = `${ore} h ${resto < 10 ? "0" : ""}${resto}`;
  }
  if (minuti < 0 && assoluto > 0) return MENO + testo;
  return conSegno && assoluto > 0 ? "+" + testo : testo;
};

/** Spazio non separabile: il numero non va a capo lontano dalla sua unità ("−1 / ora"). */
const NBSP = " ";

/** Durata a parole: "0 min", "45 min", "1 ora", "2 ore", "1 ora e 1 min", "9 ore e 5 min". */
export const durataDiscorsiva = (minuti: number): string => {
  const v = Math.abs(Math.round(minuti));
  const ore = Math.floor(v / 60);
  const resto = v % 60;
  if (ore === 0) return `${resto}${NBSP}min`;
  const testoOre = `${ore}${NBSP}${ore === 1 ? "ora" : "ore"}`;
  return resto ? `${testoOre} e ${resto}${NBSP}min` : testoOre;
};

/**
 * Durata nelle frasi e nel pannello della giornata: a parole con l'unità "ore" ("−1 ora e 45 min"),
 * in centesimi come formatDurata ("−1,75"). Il segno meno è quello tipografico.
 */
export const durataTesto = (minuti: number, unita: TUnitaPresenze, conSegno = false): string => {
  if (unita === "centesimi") return formatDurata(minuti, unita, conSegno);
  const assoluto = Math.abs(Math.round(minuti));
  const testo = durataDiscorsiva(assoluto);
  if (minuti < 0 && assoluto > 0) return MENO + testo;
  return conSegno && assoluto > 0 ? "+" + testo : testo;
};

/**
 * Scostamenti nelle frasi: sotto l'ora in minuti ("18 min"); da un'ora in su a parole con l'unità
 * "ore" ("1 ora e 58 min"), in centesimi con l'altra ("1,97").
 */
export const formatMinutiBrevi = (minuti: number, unita: TUnitaPresenze): string => {
  const assoluto = Math.abs(Math.round(minuti));
  if (assoluto < 60) return `${assoluto} min`;
  return unita === "centesimi" ? formatDurata(assoluto, unita) : durataDiscorsiva(assoluto);
};

/** "09:00–13:30" */
export const orario = (da: string, a: string): string => `${da}${TRATTINO}${a}`;

/** Minuti di "AAAA-MM-GG HH:mm" dalla mezzanotte di "AAAA-MM-GG" (negativi il giorno prima). */
export const minutiDalGiorno = (dataOra: string, giorno: string): number => {
  const giorni =
    (Date.UTC(+dataOra.substr(0, 4), +dataOra.substr(5, 2) - 1, +dataOra.substr(8, 2)) -
      Date.UTC(+giorno.substr(0, 4), +giorno.substr(5, 2) - 1, +giorno.substr(8, 2))) /
    86400000;
  return Math.round(giorni) * 1440 + +dataOra.substr(11, 2) * 60 + +dataOra.substr(14, 2);
};

/** Minuti tra due "AAAA-MM-GG HH:mm". */
export const minutiTra = (da: string, a: string): number =>
  minutiDalGiorno(a, da.substr(0, 10)) - minutiDalGiorno(da, da.substr(0, 10));

/** "martedì 6 ottobre 2026" */
export const dataEstesa = (data: string): string => moment(data).format("dddd D MMMM YYYY");

/** "Martedì 6 ottobre 2026" */
export const dataEstesaMaiuscola = (data: string): string => maiuscola(dataEstesa(data));

/** "lunedì 5" */
export const giornoBreve = (data: string): string => moment(data).format("dddd D");

/** "lun 5" */
export const giornoCorto = (data: string): string => moment(data).format("ddd D");

export const maiuscola = (testo: string): string =>
  testo ? testo.charAt(0).toUpperCase() + testo.slice(1) : testo;

/**
 * Orario rispetto al giorno mostrato: "08:50" nello stesso giorno, altrimenti con il giorno
 * ("lunedì 5 alle 19:33").
 */
export const quando = (dataOra: string, giorno: string): string =>
  dataOra.substr(0, 10) === giorno
    ? ora(dataOra)
    : `${giornoBreve(dataOra.substr(0, 10))} alle ${ora(dataOra)}`;

/** Etichetta del giorno nelle liste: "Oggi, martedì 6 ottobre", "Ieri, ...", "Venerdì 2 ottobre". */
export const etichettaGiorno = (data: string, oggi: string): string => {
  const testo = moment(data).format("dddd D MMMM");
  if (data === oggi) return `Oggi, ${testo}`;
  if (data === moment(oggi).subtract(1, "day").format("YYYY-MM-DD")) return `Ieri, ${testo}`;
  return maiuscola(testo);
};

/** "Ottobre 2026" */
export const etichettaMese = (mese: string): string =>
  maiuscola(moment(`${mese}-01`).format("MMMM YYYY"));

export const plurale = (n: number, uno: string, molti: string): string =>
  `${n} ${n === 1 ? uno : molti}`;

/** "dalle 14:40" nello stesso giorno, "da lunedì 5 alle 21:58" altrimenti. */
const dalleQuando = (dataOra: string, giorno: string): string =>
  dataOra.substr(0, 10) === giorno ? `dalle ${ora(dataOra)}` : `da ${quando(dataOra, giorno)}`;

// ------------------------------------------------------------------ ricerca e filtri

/** Ricerca per nome, senza maiuscole e accenti, parole in qualunque ordine. */
export const corrispondeNome = (
  persona: Pick<TPersonaPresenze, "nome" | "cognome">,
  ricerca: string,
): boolean => {
  const parole = normalizza(ricerca).split(/\s+/).filter(Boolean);
  if (!parole.length) return true;
  const nome = normalizza(`${persona.cognome} ${persona.nome}`);
  return parole.every(p => nome.indexOf(p) >= 0);
};

const STATI_DEL_FILTRO: Record<TFiltroGiornata, TStatoPersonaGiorno[]> = {
  tutti: [],
  in_servizio: ["in_servizio"],
  da_iniziare: ["da_iniziare"],
  usciti: ["uscito"],
  non_timbrati: ["non_timbrato"],
  assenti: ["assente", "riposo"],
  da_gestire: [],
};

export const corrispondeAlFiltro = (giorno: TGiornoPresenze, filtro: TFiltroGiornata): boolean => {
  if (filtro === "tutti") return true;
  if (filtro === "da_gestire") return giorno.totali.anomalie > 0;
  return STATI_DEL_FILTRO[filtro].indexOf(giorno.stato.codice) >= 0;
};

// ------------------------------------------------------------------ stato della persona

export type TClasseStato = "live" | "done" | "abs" | "anom" | "todo" | "rest";

/** Riga sotto il nome: lo stato lo decide il backend, qui solo il testo. */
export const testoStatoPersona = (
  giorno: TGiornoPresenze,
): { testo: string; classe: TClasseStato } => {
  const { codice, dalle } = giorno.stato;
  switch (codice) {
    case "in_servizio":
      return {
        testo: dalle ? `In servizio ${dalleQuando(dalle, giorno.data)}` : "In servizio",
        classe: "live",
      };
    case "uscito":
      return {
        testo: dalle
          ? `Uscita ${dalle.substr(0, 10) === giorno.data ? "alle " : ""}${quando(
              dalle,
              giorno.data,
            )}`
          : "Uscito",
        classe: "done",
      };
    case "assente": {
      const giornaliero = giorno.giustificativi.find(
        g => g.unita === "GIORNI" && g.stato === "accettata",
      );
      return {
        testo: giornaliero ? `${giornaliero.nome}, tutto il giorno` : "Assente, turni coperti",
        classe: "abs",
      };
    }
    case "non_timbrato":
      return { testo: "Non timbrato", classe: "anom" };
    case "da_iniziare":
      return {
        testo: dalle ? `Turno ${dalleQuando(dalle, giorno.data)}` : "Turno da iniziare",
        classe: "todo",
      };
    case "riposo":
      return { testo: "Riposo", classe: "rest" };
    default:
      return { testo: "Nessun turno", classe: "todo" };
  }
};

export type TColorePillola = "blu" | "verde" | "grigio" | "rosso" | "ambra";

export const ETICHETTE_STATO_TURNO: Record<
  TStatoTurnoPresenze,
  { testo: string; colore: TColorePillola }
> = {
  da_svolgere: { testo: "Da svolgere", colore: "blu" },
  in_corso: { testo: "In corso", colore: "verde" },
  timbrato: { testo: "Timbrato", colore: "grigio" },
  mancata_timbratura: { testo: "Non timbrato", colore: "rosso" },
  non_lavorativo: { testo: "Riposo", colore: "grigio" },
  coperto: { testo: "Coperto", colore: "grigio" },
};

export const etichettaStatoTurno = (
  turno: TTurnoPresenze,
): { testo: string; colore: TColorePillola } => {
  if (turno.stato === "coperto") {
    return {
      testo:
        turno.coperto_da === "timbratura_singola"
          ? "Timbratura singola"
          : "Coperto da giustificativo",
      colore: "grigio",
    };
  }
  if (turno.stato === "non_lavorativo" && turno.codice_sistema === "RECUPERO_BANCA_ORE") {
    return { testo: "Recupero", colore: "grigio" };
  }
  return ETICHETTE_STATO_TURNO[turno.stato];
};

/** Titolo del turno: orario, oppure il nome per riposo e recupero. */
export const titoloTurno = (turno: TTurnoPresenze): string => {
  if (!turno.lavorativo)
    return turno.codice_sistema === "RECUPERO_BANCA_ORE" ? "Recupero" : "Riposo";
  const base = `Turno ${orario(turno.ora_inizio, turno.ora_fine)}`;
  if (!turno.visibile) return `Straordinario ${orario(turno.ora_inizio, turno.ora_fine)}`;
  return base;
};

// ------------------------------------------------------------------ giustificativi

/**
 * Il permesso si vede in questo giorno, ma è del turno di un altro giorno (di_competenza false):
 * nascosto dove non si mostrano nemmeno i turni "del giorno prima" (riga del mese, settimana della Home).
 */
export const diUnAltroGiorno = (g: TGiustificativoGiorno): boolean => g.di_competenza === false;

/**
 * "Permessi 17:00–18:00". Con il giorno mostrato, per i permessi di un notturno: "Permessi 00:00–01:00
 * di sabato 24" nel giorno del turno, "Permessi 00:00–01:00 del turno di venerdì 23" il giorno dopo.
 */
export const testoGiustificativo = (g: TGiustificativoGiorno, giorno?: string): string => {
  if (!(g.unita === "ORE" && g.dalle && g.alle)) return `${g.nome}, tutto il giorno`;
  const base = `${g.nome} ${orario(g.dalle, g.alle)}`;
  if (!giorno) return base;
  if (diUnAltroGiorno(g) && g.giorni_competenza && g.giorni_competenza.length) {
    return `${base} del turno di ${giornoBreve(g.giorni_competenza[0])}`;
  }
  const dataInizio = g.inizio ? g.inizio.substr(0, 10) : giorno;
  return dataInizio !== giorno ? `${base} di ${giornoBreve(dataInizio)}` : base;
};

/** Durata sempre in ore e minuti: "04:35", "00:45". */
export const oreMinuti = (minuti: number): string => {
  const v = Math.max(0, Math.round(minuti));
  const ore = Math.floor(v / 60);
  const resto = v % 60;
  return `${ore < 10 ? "0" : ""}${ore}:${resto < 10 ? "0" : ""}${resto}`;
};

/** Durata: sotto l'ora in minuti ("45 min"), da un'ora in su in ore e minuti ("01:30"). */
export const durataHHMM = (minuti: number): string => {
  if (minuti < 60) return `${minuti} min`;
  const ore = Math.floor(minuti / 60);
  const resto = minuti % 60;
  return `${ore < 10 ? "0" : ""}${ore}:${resto < 10 ? "0" : ""}${resto}`;
};

/** Testo del pannello: per i permessi a ore anche la durata nel giorno, calcolata dal backend. */
export const testoGiustificativoConDurata = (g: TGiustificativoGiorno, giorno?: string): string => {
  const testo = testoGiustificativo(g, giorno);
  return g.unita === "ORE" && g.minuti !== null && g.minuti !== undefined
    ? `${testo}, ${durataDiscorsiva(g.minuti)}`
    : testo;
};

/** "Dal 5 al 9 ottobre" per le richieste di più giorni, altrimenti vuoto. */
export const periodoGiustificativo = (g: Pick<TGiustificativoGiorno, "dal" | "al">): string => {
  const dal = g.dal.substr(0, 10);
  const al = g.al.substr(0, 10);
  if (dal === al) return "";
  const da = moment(dal);
  const a = moment(al);
  return da.month() === a.month()
    ? `Dal ${da.format("D")} al ${a.format("D MMMM")}`
    : `Dal ${da.format("D MMMM")} al ${a.format("D MMMM")}`;
};

/** "13 ottobre alle 10:22" da "2026-10-13 10:22". */
const giornoEOra = (dataOra: string): string => {
  const ora = dataOra.substr(11, 5);
  const giorno = moment(dataOra.substr(0, 10)).format("D MMMM");
  return ora ? `${giorno} alle ${ora}` : giorno;
};

/**
 * Ultimo stato del giustificativo e chi l'ha gestito:
 * "Accettata da Mario Rossi il 13 ottobre alle 10:22", "In attesa, inserita da Nicolò Notaro il 13 ottobre
 * alle 08:12". Per i dati vecchi senza autore: "Accettata il 13 ottobre…", "In attesa dal 13 ottobre…".
 */
export const ultimoStatoGiustificativo = (g: TGiustificativoGiorno): string => {
  const accettata = g.stato === "accettata";
  const stato = g.ultimo_stato || (accettata ? "Accettata" : "In attesa");
  const quando = g.aggiornata_il ? giornoEOra(g.aggiornata_il) : null;
  if (accettata) {
    return [stato, g.aggiornata_da ? `da ${g.aggiornata_da}` : "", quando ? `il ${quando}` : ""]
      .filter(Boolean)
      .join(" ");
  }
  if (g.aggiornata_da) {
    return `${stato}, inserita da ${g.aggiornata_da}${quando ? ` il ${quando}` : ""}`;
  }
  return quando ? `${stato} dal ${quando}` : stato;
};

export const metaGiustificativo = (g: TGiustificativoGiorno): string => {
  const parti: string[] = [];
  const periodo = periodoGiustificativo(g);
  if (periodo) parti.push(periodo);
  parti.push(ultimoStatoGiustificativo(g));
  return parti.join(", ");
};

// ------------------------------------------------------------------ segnalazioni

export const CODICI = {
  TIMEBANK: "TIMEBANK_NON_APPROVATA",
  MANCATA_TIMBRATURA: "MANCATA_TIMBRATURA",
  GIORNATA_CHIUSURA: "GIORNATA_CHIUSURA",
  SENZA_TURNO: "TURNO_NON_PROGRAMMATO",
  MANCATA_CHIUSURA: "MANCATA_CHIUSURA",
  DURATA_ECCESSIVA: "DURATA_ECCESSIVA",
  USCITA_SUPERATA: "USCITA_PREVISTA_SUPERATA",
  GIUSTIFICATIVO_CON_PRESENZA: "GIUSTIFICATIVO_CON_PRESENZA",
  PERMESSO_CON_PRESENZA: "PERMESSO_CON_PRESENZA",
};

/** Gruppi di codici del filtro "Tipo" in Da gestire. */
export type TTipoSegnalazione =
  | "tutte"
  | "timebank"
  | "mancate"
  | "chiusura"
  | "senza_turno"
  | "altre";

export const tipoSegnalazione = (codice: string): TTipoSegnalazione => {
  switch (codice) {
    case CODICI.TIMEBANK:
      return "timebank";
    case CODICI.MANCATA_TIMBRATURA:
    case CODICI.GIORNATA_CHIUSURA:
      return "mancate";
    case CODICI.MANCATA_CHIUSURA:
    case CODICI.DURATA_ECCESSIVA:
      return "chiusura";
    case CODICI.SENZA_TURNO:
      return "senza_turno";
    default:
      return "altre";
  }
};

/** Una segnalazione con quello che serve per descriverla e gestirla. */
export interface TContestoSegnalazione {
  chiave: string;
  codice: string;
  livello: "anomalia" | "avviso";
  data: string;
  descrizione: string;
  dettagli: Record<string, unknown>;
  persona: TPersonaPresenze;
  presenza: TPresenzaVista | null;
  turno: TTurnoPresenze | null;
  bloccato: boolean;
}

/** Contesti delle segnalazioni di un giorno della persona (Giornata e pannello). */
export const contestiDelGiorno = (
  persona: TPersonaPresenze,
  giorno: TGiornoPresenze,
): TContestoSegnalazione[] =>
  giorno.segnalazioni.map((s, i) => {
    const presenza = s.presence_id
      ? giorno.presenze.find(p => p.id === s.presence_id) || null
      : null;
    const turno = s.shift_planning_id
      ? giorno.turni.find(t => t.id === s.shift_planning_id) || null
      : presenza && presenza.turno_id
      ? giorno.turni.find(t => t.id === presenza.turno_id) || null
      : null;
    return {
      chiave: `${s.codice}:${s.data}:${persona.id}:${s.presence_id ?? s.shift_planning_id ?? i}`,
      codice: s.codice,
      livello: s.livello,
      data: s.data,
      descrizione: s.descrizione,
      dettagli: s.dettagli || {},
      persona,
      presenza,
      turno,
      bloccato: presenza ? !presenza.modificabile : giorno.bloccato,
    };
  });

const testoDettaglio = (dettagli: Record<string, unknown>, chiave: string): string => {
  const valore = dettagli[chiave];
  return typeof valore === "string" ? valore : "";
};

/** "09:00-13:30" del backend in "09:00–13:30". */
const orarioDaDettagli = (testo: string): string => testo.replace("-", TRATTINO);

const deltaTimebank = (c: TContestoSegnalazione): number => c.presenza?.timebank?.delta_minuti ?? 0;

/** Segnalazioni che una richiesta in attesa può sanare (dettagli.permesso_in_attesa dal backend). */
const CODICI_CON_RICHIESTA_IN_ATTESA = [
  CODICI.TIMEBANK,
  CODICI.MANCATA_TIMBRATURA,
  CODICI.GIORNATA_CHIUSURA,
];

/**
 * Richiesta in attesa che, se accettata, sanerebbe la segnalazione (punto 10): la segnalazione si
 * mostra come "Permessi da accettare" invece che come turno parziale o mancata timbratura.
 */
export const permessoInAttesa = (
  c: Pick<TContestoSegnalazione, "codice" | "dettagli">,
): TPermessoInAttesa | null => {
  if (CODICI_CON_RICHIESTA_IN_ATTESA.indexOf(c.codice) < 0) return null;
  const valore = c.dettagli.permesso_in_attesa as TPermessoInAttesa | undefined;
  return valore && typeof valore === "object" && typeof valore.richiesta_id === "number"
    ? valore
    : null;
};

/** Durata dell'intera richiesta: "2 ore", "45 min" per quelle a ore, "5 giorni" per quelle a giorni. */
export const durataRichiestaInAttesa = (r: TPermessoInAttesa): string => {
  if (r.unita === "ORE") {
    return durataDiscorsiva(
      moment(r.al, "YYYY-MM-DD HH:mm").diff(moment(r.dal, "YYYY-MM-DD HH:mm"), "minutes"),
    );
  }
  const giorni = moment(r.al.substr(0, 10)).diff(moment(r.dal.substr(0, 10)), "days") + 1;
  return plurale(giorni, "giorno", "giorni");
};

/**
 * "Permessi 09:00–11:00 (2 ore)", "Ferie dal 5 al 9 ottobre (5 giorni)", "Ferie (1 giorno)":
 * con la durata, come nella scheda del giustificativo.
 */
export const testoRichiestaInAttesa = (r: TPermessoInAttesa): string => {
  const durata = durataRichiestaInAttesa(r);
  if (r.unita === "ORE" && r.dalle && r.alle) {
    return `${r.tipo} ${orario(r.dalle, r.alle)} (${durata})`;
  }
  const periodo = periodoGiustificativo(r);
  const testo = periodo
    ? `${r.tipo} ${periodo.charAt(0).toLowerCase()}${periodo.slice(1)}`
    : r.tipo;
  return `${testo} (${durata})`;
};

/** Con la banca ore attiva non esiste lo straordinario: "banca ore" positiva o negativa. */
export const titoloSegnalazione = (
  c: TContestoSegnalazione,
  modalita: TModalitaPresenze,
): string => {
  const inAttesa = permessoInAttesa(c);
  if (inAttesa) return `${inAttesa.tipo} da accettare`;
  switch (c.codice) {
    case CODICI.TIMEBANK: {
      if (modalita === "banca_ore") return "Banca ore da approvare";
      const azioni = c.presenza?.timebank?.azioni || [];
      if (azioni.indexOf("crea_rol") >= 0 || deltaTimebank(c) < 0)
        return "Turno parziale da gestire";
      return "Straordinario da approvare";
    }
    case CODICI.MANCATA_TIMBRATURA:
      return "Mancata timbratura";
    case CODICI.GIORNATA_CHIUSURA:
      return "Turno in un giorno di chiusura";
    case CODICI.SENZA_TURNO:
      return "Timbratura senza turno";
    case CODICI.MANCATA_CHIUSURA:
      return "Mancata chiusura";
    case CODICI.DURATA_ECCESSIVA:
      return "Presenza troppo lunga";
    case CODICI.USCITA_SUPERATA:
      return "Uscita prevista superata";
    case CODICI.GIUSTIFICATIVO_CON_PRESENZA:
      return "Giustificativo con presenza";
    case CODICI.PERMESSO_CON_PRESENZA:
      return "Permesso con presenza";
    default:
      return c.descrizione;
  }
};

/** Testo breve per i chip della Giornata e di Da gestire. */
export const chipSegnalazione = (
  c: TContestoSegnalazione,
  modalita: TModalitaPresenze,
  unita: TUnitaPresenze,
): string => {
  const inAttesa = permessoInAttesa(c);
  if (inAttesa) return `${inAttesa.tipo} in attesa`;
  switch (c.codice) {
    case CODICI.TIMEBANK: {
      const delta = formatDurata(deltaTimebank(c), unita, true);
      if (modalita === "banca_ore") return `Banca ore ${delta}`;
      return deltaTimebank(c) < 0 ? `Turno parziale ${delta}` : `Straordinario ${delta}`;
    }
    case CODICI.MANCATA_TIMBRATURA: {
      const turno = testoDettaglio(c.dettagli, "turno") || (c.turno ? c.turno.ora_inizio : "");
      return turno ? `Non timbrato ${turno.substr(0, 5)}` : "Non timbrato";
    }
    case CODICI.GIORNATA_CHIUSURA:
      return "Sede chiusa";
    case CODICI.SENZA_TURNO:
      return "Senza turno";
    case CODICI.MANCATA_CHIUSURA:
      return "Mancata chiusura";
    case CODICI.DURATA_ECCESSIVA:
      return "Presenza lunga";
    case CODICI.USCITA_SUPERATA:
      return "Uscita superata";
    case CODICI.GIUSTIFICATIVO_CON_PRESENZA:
      return "Giustificativo e presenza";
    case CODICI.PERMESSO_CON_PRESENZA:
      return "Presenza nel permesso";
    default:
      return c.descrizione;
  }
};

/** "alle 19:33" nello stesso giorno, "martedì 6 alle 08:50" altrimenti. */
const alle = (dataOra: string, giorno: string): string =>
  dataOra.substr(0, 10) === giorno ? `alle ${ora(dataOra)}` : quando(dataOra, giorno);

const orariPresenza = (p: TPresenzaVista, giorno: string): string => {
  if (!p.uscita) return `aperta ${alle(p.entrata, giorno)}`;
  if (p.entrata.substr(0, 10) === giorno && p.uscita.substr(0, 10) === giorno) {
    return orario(ora(p.entrata), ora(p.uscita));
  }
  return `dalle ${quando(p.entrata, giorno)} a ${quando(p.uscita, giorno)}`;
};

/** Descrizione in italiano semplice, con gli orari che servono per decidere. */
export const descrizioneSegnalazione = (
  c: TContestoSegnalazione,
  unita: TUnitaPresenze,
): string => {
  const base = descrizioneDiBase(c, unita);
  const r = permessoInAttesa(c);
  if (!r) return base;
  let copre = "copre il turno";
  if (c.codice === CODICI.TIMEBANK) {
    copre = r.copre_tutto
      ? "copre il tempo mancante"
      : `copre ${formatMinutiBrevi(r.minuti_coperti, unita)} su ${formatMinutiBrevi(
          r.minuti_scoperti,
          unita,
        )} mancanti`;
  }
  return `${base} ${testoRichiestaInAttesa(r)} in attesa di accettazione: ${copre}.`;
};

export interface TRigaSegnalazione {
  etichetta: string;
  valore: string;
}

/** Dati della segnalazione in righe, più una frase evidenziata con l'effetto dell'azione. */
export interface TDettaglioSegnalazione {
  righe: TRigaSegnalazione[];
  esito: string | null;
}

/**
 * Timbrature da approvare e segnalazioni con una richiesta in attesa, in righe (Turno, Timbrata,
 * Scostamento, Richiesta) invece che in una frase sola; sotto, cosa succede accettando la richiesta.
 * null per le altre segnalazioni, che restano una frase (descrizioneSegnalazione).
 */
export const dettaglioSegnalazione = (
  c: TContestoSegnalazione,
  modalita: TModalitaPresenze,
  unita: TUnitaPresenze,
): TDettaglioSegnalazione | null => {
  const r = permessoInAttesa(c);
  const righe: TRigaSegnalazione[] = [];

  if (c.codice === CODICI.TIMEBANK) {
    const p = c.presenza;
    const tb = p?.timebank;
    if (!p || !tb) return null;
    if (tb.turno.inizio && tb.turno.fine) {
      righe.push({ etichetta: "Turno", valore: orario(ora(tb.turno.inizio), ora(tb.turno.fine)) });
    }
    righe.push({ etichetta: "Timbrata", valore: orariPresenza(p, c.data) });
    righe.push({
      etichetta: modalita === "banca_ore" ? "Banca ore" : "Scostamento",
      valore: durataTesto(tb.delta_minuti, unita, true),
    });
    if (tb.permessi_minuti > 0) {
      righe.push({
        etichetta: "Permessi",
        valore: `${formatMinutiBrevi(tb.permessi_minuti, unita)} già considerati`,
      });
    }
  } else if (r) {
    const turno = testoDettaglio(c.dettagli, "turno");
    if (turno) righe.push({ etichetta: "Turno", valore: orarioDaDettagli(turno) });
    righe.push({ etichetta: "Timbrata", valore: "nessuna timbratura" });
  } else {
    return null;
  }

  if (!r) return { righe, esito: null };

  righe.push({ etichetta: "Richiesta", valore: `${testoRichiestaInAttesa(r)}, in attesa` });
  let esito = "Accettandola copre il turno.";
  if (c.codice === CODICI.TIMEBANK) {
    esito = r.copre_tutto
      ? "Accettandola copre tutto il tempo mancante."
      : `Accettandola copre ${formatMinutiBrevi(r.minuti_coperti, unita)} su ${formatMinutiBrevi(
          r.minuti_scoperti,
          unita,
        )} mancanti.`;
  }
  return { righe, esito };
};

const descrizioneDiBase = (c: TContestoSegnalazione, unita: TUnitaPresenze): string => {
  const p = c.presenza;
  switch (c.codice) {
    case CODICI.TIMEBANK: {
      if (!p || !p.timebank) return c.descrizione;
      const tb = p.timebank;
      const parti: string[] = [];
      const timbrata = `Timbrata ${orariPresenza(p, c.data)}`;
      parti.push(
        tb.turno.inizio && tb.turno.fine
          ? `${timbrata} su un turno ${orario(ora(tb.turno.inizio), ora(tb.turno.fine))}.`
          : `${timbrata}.`,
      );
      parti.push(`Scostamento ${durataTesto(tb.delta_minuti, unita, true)}.`);
      if (tb.permessi_minuti > 0) {
        parti.push(
          `Permessi a ore già considerati: ${formatMinutiBrevi(tb.permessi_minuti, unita)}.`,
        );
      }
      return parti.join(" ");
    }
    case CODICI.MANCATA_TIMBRATURA: {
      const turno = testoDettaglio(c.dettagli, "turno");
      return turno
        ? `Nessuna timbratura collegata al turno ${orarioDaDettagli(turno)}.`
        : "Nessuna timbratura collegata al turno.";
    }
    case CODICI.GIORNATA_CHIUSURA: {
      const turno = testoDettaglio(c.dettagli, "turno");
      return `Turno ${
        turno ? orarioDaDettagli(turno) + " " : ""
      }assegnato in un giorno di chiusura della sede, senza timbrature.`;
    }
    case CODICI.SENZA_TURNO:
      return p
        ? `Presenza ${orariPresenza(p, c.data)} senza turno assegnato.`
        : "Presenza senza turno assegnato.";
    case CODICI.MANCATA_CHIUSURA:
      if (!p) return c.descrizione;
      return p.uscita
        ? `Presenza aperta ${alle(p.entrata, c.data)} e chiusa ${alle(
            p.uscita,
            c.data,
          )}: ${durataTesto(p.minuti, unita)}.`
        : `Presenza aperta ${alle(p.entrata, c.data)}, ancora senza uscita.`;
    case CODICI.DURATA_ECCESSIVA: {
      if (!p || !p.uscita) return c.descrizione;
      const massimi =
        typeof c.dettagli.minuti_massimi === "number" ? (c.dettagli.minuti_massimi as number) : 780;
      if (c.dettagli.chiusa_automaticamente === true) {
        return `Presenza dalle ${quando(p.entrata, c.data)} chiusa automaticamente ${alle(
          p.uscita,
          c.data,
        )}: l'uscita non è stata timbrata. Correggi gli orari.`;
      }
      // il limite sempre a parole ("13 ore"), anche con la vista in centesimi
      const limite = durataTesto(massimi, "ore");
      return `Presenza ${orariPresenza(p, c.data)}: ${durataTesto(p.minuti, unita)}, ${
        p.minuti > massimi ? "oltre il" : "pari al"
      } limite di ${limite}. Verifica gli orari.`;
    }
    case CODICI.USCITA_SUPERATA: {
      const fine = testoDettaglio(c.dettagli, "fine_turno");
      return p
        ? `Presenza aperta dalle ${quando(p.entrata, c.data)}${
            fine ? `; il turno è finito alle ${fine}` : ""
          }.`
        : c.descrizione;
    }
    case CODICI.GIUSTIFICATIVO_CON_PRESENZA:
      return "Giustificativo di tutto il giorno e presenza nello stesso giorno.";
    case CODICI.PERMESSO_CON_PRESENZA: {
      const permesso = testoDettaglio(c.dettagli, "permesso");
      const minuti = typeof c.dettagli.minuti === "number" ? (c.dettagli.minuti as number) : 0;
      return `Presenza durante il permesso${permesso ? " " + orarioDaDettagli(permesso) : ""}${
        minuti ? ` per ${formatMinutiBrevi(minuti, unita)}` : ""
      }.`;
    }
    default:
      return c.descrizione;
  }
};

// ------------------------------------------------------------------ azioni

export const ETICHETTE_TIMEBANK: Record<TAzioneTimebank, string> = {
  approva: "Approva",
  rifiuta: "Rifiuta",
  approva_straordinario: "Approva straordinario",
  rifiuta_straordinario: "Rifiuta straordinario",
  crea_rol: "Crea ROL equivalente",
  conferma_turno_parziale: "Conferma turno parziale",
};

/** Esito mostrato sulla riga dopo l'azione. */
export const ESITI_TIMEBANK: Record<TAzioneTimebank, string> = {
  approva: "Approvata",
  rifiuta: "Rifiutata",
  approva_straordinario: "Straordinario approvato",
  rifiuta_straordinario: "Straordinario rifiutato",
  crea_rol: "ROL equivalente creato",
  conferma_turno_parziale: "Turno parziale confermato",
};

export const AZIONI_APPROVA: TAzioneTimebank[] = ["approva", "approva_straordinario"];
export const AZIONI_RIFIUTA: TAzioneTimebank[] = ["rifiuta", "rifiuta_straordinario"];

export type TModoCorrezione = "timebank" | "presenza";

/**
 * Quale chiamata corregge gli orari di una presenza:
 *  - presenza chiusa con turno e banca ore: presences_timebank_update (Presence/Canchangetime),
 *    gli orari timbrati restano nello storico;
 *  - altrimenti PUT /presences/{id} (Presence/Canmanageupdate), che serve anche a chiudere una
 *    presenza aperta.
 */
export const modoCorrezione = (
  presenza: TPresenzaVista,
  permessi: { cambia_orari: boolean; correggi_presenza: boolean },
): TModoCorrezione | null => {
  if (!presenza.modificabile) return null;
  if (presenza.timebank && !presenza.aperta && permessi.cambia_orari) return "timebank";
  if (permessi.correggi_presenza) return "presenza";
  return null;
};

export type TTipoAzione =
  | "timebank"
  | "inserisci_presenza"
  | "inserisci_giustificativo"
  | "correggi_orari"
  | "registra_uscita"
  | "assegna_turno"
  | "cambia_turno"
  | "elimina_presenza"
  /** accetta o rifiuta la richiesta in attesa che sanerebbe la segnalazione */
  | "stato_richiesta";

export interface TPulsanteAzione {
  tipo: TTipoAzione;
  codice?: TAzioneTimebank;
  /** solo per stato_richiesta */
  richiestaId?: number;
  stato?: "accetta" | "rifiuta";
  etichetta: string;
  principale?: boolean;
  pericolosa?: boolean;
  disattivato?: boolean;
}

export interface TPermessiAzioni {
  modifica: boolean;
  crea: boolean;
  elimina: boolean;
  cambia_orari: boolean;
  correggi_presenza: boolean;
  gestisci_richieste: boolean;
}

/**
 * Pulsanti di una segnalazione e nota da mostrare sotto. Le azioni delle timbrature da approvare
 * arrivano già decise dal backend; per le altre conta il permesso della chiamata esistente.
 */
export const azioniSegnalazione = (
  c: TContestoSegnalazione,
  permessi: TPermessiAzioni,
): { pulsanti: TPulsanteAzione[]; nota: string | null } => {
  if (c.bloccato) return { pulsanti: [], nota: "Il giorno è bloccato: non si può modificare." };

  // prima si decide la richiesta in attesa: accettata sana la segnalazione, rifiutata la lascia com'è
  const r = permessoInAttesa(c);
  if (r && permessi.gestisci_richieste) {
    return {
      pulsanti: [
        {
          tipo: "stato_richiesta",
          stato: "accetta",
          richiestaId: r.richiesta_id,
          etichetta: "Accetta richiesta",
          principale: true,
        },
        {
          tipo: "stato_richiesta",
          stato: "rifiuta",
          richiestaId: r.richiesta_id,
          etichetta: "Rifiuta richiesta",
        },
      ],
      nota: null,
    };
  }
  const azioni = azioniDiBase(c, permessi);
  if (!r) return azioni;
  return {
    pulsanti: azioni.pulsanti,
    nota: "La richiesta va accettata o rifiutata da chi gestisce i giustificativi.",
  };
};

const azioniDiBase = (
  c: TContestoSegnalazione,
  permessi: TPermessiAzioni,
): { pulsanti: TPulsanteAzione[]; nota: string | null } => {
  const pulsanti: TPulsanteAzione[] = [];
  const p = c.presenza;

  switch (c.codice) {
    case CODICI.TIMEBANK: {
      const azioni = p?.timebank?.azioni || [];
      azioni.forEach((codice, i) =>
        pulsanti.push({
          tipo: "timebank",
          codice,
          etichetta: ETICHETTE_TIMEBANK[codice],
          principale: i === 0,
        }),
      );
      if (!azioni.length) return { pulsanti, nota: "Non hai il permesso per approvare." };
      return { pulsanti, nota: null };
    }
    case CODICI.MANCATA_TIMBRATURA:
    case CODICI.GIORNATA_CHIUSURA: {
      const t = c.turno;
      let nota: string | null = null;
      if (t && permessi.crea) {
        pulsanti.push({
          tipo: "inserisci_presenza",
          etichetta: "Inserisci presenza",
          principale: true,
          disattivato: !t.inseribile,
        });
        if (!t.inseribile && t.inseribile_dalle) {
          nota = `La presenza si potrà inserire dopo la fine del turno, alle ${quando(
            t.inseribile_dalle,
            c.data,
          )}.`;
        }
      }
      if (permessi.gestisci_richieste) {
        pulsanti.push({ tipo: "inserisci_giustificativo", etichetta: "Inserisci giustificativo" });
      }
      return { pulsanti, nota };
    }
    case CODICI.SENZA_TURNO:
      if (p && permessi.modifica) {
        pulsanti.push({ tipo: "assegna_turno", etichetta: "Assegna turno", principale: true });
      }
      if (p && permessi.elimina) {
        pulsanti.push({
          tipo: "elimina_presenza",
          etichetta: "Elimina presenza",
          pericolosa: true,
        });
      }
      return { pulsanti, nota: null };
    case CODICI.MANCATA_CHIUSURA:
    case CODICI.DURATA_ECCESSIVA:
      if (p && modoCorrezione(p, permessi)) {
        pulsanti.push({ tipo: "correggi_orari", etichetta: "Correggi orari", principale: true });
      }
      if (p && permessi.elimina) {
        pulsanti.push({
          tipo: "elimina_presenza",
          etichetta: "Elimina presenza",
          pericolosa: true,
        });
      }
      return { pulsanti, nota: null };
    case CODICI.USCITA_SUPERATA:
      if (p && p.aperta && permessi.correggi_presenza) {
        pulsanti.push({ tipo: "registra_uscita", etichetta: "Registra l'uscita" });
      }
      return { pulsanti, nota: null };
    default:
      return { pulsanti, nota: null };
  }
};

/** Azioni sulla scheda di una presenza nel pannello (oltre a quelle delle segnalazioni). */
export const azioniPresenza = (p: TPresenzaVista, permessi: TPermessiAzioni): TPulsanteAzione[] => {
  if (!p.modificabile) return [];
  const pulsanti: TPulsanteAzione[] = [];
  if (modoCorrezione(p, permessi))
    pulsanti.push({ tipo: "correggi_orari", etichetta: "Correggi orari" });
  // lo straordinario ha il suo turno, creato con l'approvazione: non si cambia né si assegna
  if (permessi.modifica && !eStraordinario(p)) {
    pulsanti.push(
      p.turno_id
        ? { tipo: "cambia_turno", etichetta: "Cambia turno" }
        : { tipo: "assegna_turno", etichetta: "Assegna turno" },
    );
  }
  if (permessi.elimina)
    pulsanti.push({ tipo: "elimina_presenza", etichetta: "Elimina", pericolosa: true });
  return pulsanti;
};

/** Fine del turno della presenza, per proporre l'uscita: dal turno se c'è, altrimenti dai dettagli. */
export const uscitaProposta = (c: TContestoSegnalazione): string | null => {
  if (c.turno) return c.turno.fine;
  const p = c.presenza;
  const fine = testoDettaglio(c.dettagli, "fine_turno");
  if (!p || !fine) return null;
  const giorno = p.entrata.substr(0, 10);
  const candidata = `${giorno} ${fine}`;
  return candidata > p.entrata
    ? candidata
    : `${moment(giorno).add(1, "day").format("YYYY-MM-DD")} ${fine}`;
};

// ------------------------------------------------------------------ presenze e storico

export const ORIGINI: Record<string, string> = {
  app: "App",
  ipad: "iPad",
  web: "Web",
  backoffice: "Gestionale",
  artificial: "Inserita a mano",
  nfc: "Badge NFC",
};

export const testoOrigine = (origine: string | null): string =>
  origine ? ORIGINI[origine.toLowerCase()] || maiuscola(origine) : "Origine non indicata";

/** Scostamento dell'ingresso o dell'uscita dal turno (minuti positivi = più lavoro). */
export const testoScostamento = (
  minuti: number,
  verso: "ingresso" | "uscita",
  unita: TUnitaPresenze,
): string => {
  if (minuti === 0) return "puntuale";
  const quanto = formatMinutiBrevi(minuti, unita);
  if (verso === "ingresso")
    return minuti > 0 ? `in anticipo di ${quanto}` : `in ritardo di ${quanto}`;
  return minuti > 0 ? `oltre la fine di ${quanto}` : `in anticipo di ${quanto}`;
};

export const STATI_TIMEBANK: Record<string, { testo: string; colore: TColorePillola }> = {
  in_attesa: { testo: "Da approvare", colore: "ambra" },
  approvata: { testo: "Approvata", colore: "grigio" },
  rifiutata: { testo: "Rifiutata", colore: "rosso" },
};

export const EVENTI_STORICO: Record<string, string> = {
  start: "Entrata",
  end: "Uscita",
  update: "Modifica",
  delete: "Eliminazione",
};

/** Il giorno ha un'assenza di tutto il giorno da mostrare al posto della linea. */
export const assenzaDelGiorno = (giorno: TGiornoPresenze): TGiustificativoGiorno | null => {
  // la notte prima (presenze e turni iniziati il giorno prima) non toglie l'assenza del giorno
  if (giorno.presenze.some(p => !p.giorno_prima)) return null;
  if (giorno.turni.some(t => !t.giorno_prima && t.lavorativo && t.stato !== "coperto")) return null;
  return giorno.giustificativi.find(g => g.unita === "GIORNI") || null;
};

/** Solo riposo o recupero nel giorno, senza presenze iniziate nel giorno. */
export const soloRiposo = (giorno: TGiornoPresenze): boolean => {
  const delGiorno = giorno.turni.filter(t => !t.giorno_prima);
  return (
    !giorno.presenze.some(p => !p.giorno_prima) &&
    delGiorno.length > 0 &&
    delGiorno.every(t => !t.lavorativo) &&
    !giorno.giustificativi.some(g => g.unita === "GIORNI")
  );
};

/** Presenza di tipo straordinario: ha un colore suo sulla linea e nel mese. */
export const eStraordinario = (p: Pick<TPresenzaVista, "tipo">): boolean =>
  p.tipo.codice_sistema === "STRAORDINARIO";

/**
 * Colore pieno del giustificativo, schiarito verso il bianco: copre il turno che sta sotto,
 * così le due scritte non si sovrappongono.
 */
export const tintaPiena = (colore: string | null, peso = 0.45): string => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec((colore || "").trim());
  const [r, g, b] = m ? [m[1], m[2], m[3]].map(x => parseInt(x, 16)) : [229, 158, 110];
  const mescola = (c: number): number => Math.round(c * peso + 255 * (1 - peso));
  return `rgb(${mescola(r)}, ${mescola(g)}, ${mescola(b)})`;
};

/** Segnalazione legata alla presenza: anomalia o avviso (la banca ore da approvare va nei chip). */
export const livelloPresenza = (
  giorno: Pick<TGiornoPresenze, "segnalazioni">,
  presenzaId: number,
): "anomalia" | "avviso" | null => {
  const sue = giorno.segnalazioni.filter(
    s => s.presence_id === presenzaId && s.codice !== CODICI.TIMEBANK,
  );
  if (sue.some(s => s.livello === "anomalia")) return "anomalia";
  if (sue.length) return "avviso";
  return null;
};

/** Contratto del giorno in una riga: "40 h su 5 giorni, 8 h al giorno, timbratura singola". */
export const descrizioneContrattoGiorno = (contratto: {
  ore_settimanali: number | null;
  giorni_settimanali: number | null;
  ore_giornaliere: number | null;
  single_badge: boolean;
  forfettizzato?: boolean | null;
  tipologia: { nome: string } | null;
}): string => {
  const numero = (v: number): string =>
    Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100).replace(".", ",");
  const parti: string[] = [];
  if (contratto.ore_settimanali !== null) {
    parti.push(
      contratto.giorni_settimanali
        ? `${numero(contratto.ore_settimanali)} h su ${numero(contratto.giorni_settimanali)} giorni`
        : `${numero(contratto.ore_settimanali)} h a settimana`,
    );
  } else {
    parti.push("senza ore settimanali");
  }
  if (contratto.ore_giornaliere) parti.push(`${numero(contratto.ore_giornaliere)} h al giorno`);
  if (contratto.tipologia) parti.push(`tipologia ${contratto.tipologia.nome}`);
  if (contratto.single_badge) parti.push("timbratura singola");
  if (contratto.forfettizzato) parti.push("forfettizzato");
  return parti.join(", ");
};
